import React from 'react';

/** Render `**text**` markdown markers as actual <strong> elements. */
export function renderMarkdownBold(text: string): React.ReactNode {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**')) {
            return <strong key={i}>{p.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
    });
}

/**
 * Combined renderer: applies **markdown bold** + term highlighting.
 * Bold markers wrap text in <strong>; matched terms get colored span.
 */
export function renderBoldAndTerms(
    text: string,
    terms: string[],
    colorClass: string,
): React.ReactNode {
    if (!text) return text;
    // Split by **bold** markers first
    const boldParts = text.split(/(\*\*[^*]+\*\*)/g);
    return boldParts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            const inner = part.slice(2, -2);
            return <strong key={i}>{highlightTermsInText(inner, terms, colorClass)}</strong>;
        }
        return <React.Fragment key={i}>{highlightTermsInText(part, terms, colorClass)}</React.Fragment>;
    });
}

/**
 * Parse matched rules string into source/target pairs.
 * Format: "Source" -> "Target" (one per line)
 * Supports multiple quoted sources: "S1", "S2" -> Target
 */
export const parseMatchedRulesString = (rulesText: string): { source: string; target: string }[] => {
    if (!rulesText) return [];
    const rules: { source: string; target: string }[] = [];

    for (const line of rulesText.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // Split by -> or arrow
        const arrowIdx = trimmed.search(/\s*(?:->|\u2192)\s*/);
        if (arrowIdx === -1) continue;

        const leftPart = trimmed.substring(0, arrowIdx).trim();
        const rightPart = trimmed.substring(trimmed.indexOf('>', arrowIdx) + 1).trim();

        // Strip quotes from target
        const target = rightPart.replace(/^"|"$/g, '').trim();
        if (!target) continue;

        // Parse left side: may have multiple quoted sources separated by commas
        const sourceMatches = leftPart.match(/"([^"]+)"/g);
        if (sourceMatches && sourceMatches.length > 0) {
            for (const quoted of sourceMatches) {
                const source = quoted.replace(/^"|"$/g, '').trim();
                if (source) {
                    rules.push({ source, target });
                }
            }
        } else {
            // No quotes, treat entire left as single source
            const source = leftPart.trim();
            if (source) {
                rules.push({ source, target });
            }
        }
    }

    return rules;
};

/** All dash-like Unicode characters */
const DASH_CHARS = '\u002D\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE58\uFE63\uFF0D';
const DASH_REGEX = new RegExp(`[${DASH_CHARS}]`, 'g');

/** Normalize all dash variants to regular hyphen */
const normalizeDashes = (s: string) => s.replace(DASH_REGEX, '-');

/**
 * Highlight matching terms in text with colored spans.
 * Handles em dash / en dash / hyphen variants transparently.
 * Dung chung cho AuditReport va ComparisonTable.
 */
export const highlightTermsInText = (
    text: string,
    terms: string[],
    colorClass: string
): React.ReactNode => {
    if (!text || terms.length === 0) return text;

    // Sort terms by length (longest first) to avoid partial matches
    const sortedTerms = [...terms].sort((a, b) => b.length - a.length);

    // Build regex: escape each term, then replace escaped hyphens with a dash character class
    const dashClass = `[${DASH_CHARS}]`;
    const escaped = sortedTerms.map(t => {
        const esc = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Replace literal escaped hyphen (\-) with dash character class
        return esc.replace(/\\-|-/g, dashClass);
    });
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');

    const parts = text.split(regex);
    if (parts.length === 1) return text;

    // Normalize dashes when checking membership
    const termSet = new Set(sortedTerms.map(t => normalizeDashes(t.toLowerCase())));
    return parts.map((part, i) => {
        if (termSet.has(normalizeDashes(part.toLowerCase()))) {
            return (
                <span key={i} className={`${colorClass} px-0.5 rounded font-semibold`}>
                    {part}
                </span>
            );
        }
        return part;
    });
};
